import type { BlockPromptContext } from './types.ts';
import { buildProgressionClause, buildExampleClause, buildCrossMonthClause } from './types.ts';
import { buildPersonalizationClause } from './personalization-helpers.ts';
import { SOURCE_LANGUAGES, TARGET_LANGUAGES } from '../../constants/languages.ts';

function buildPreviousReadingThemesClause(ctx: BlockPromptContext): string {
  if (!ctx.previousMonthsReadingThemes || ctx.previousMonthsReadingThemes.length === 0) return '';
  return `

## Reading Passages From Previous Months
${ctx.previousMonthsReadingThemes.map((t) => `- ${t}`).join('\n')}

**Rule:** Use a different text type and topic combination than those listed above. Vary the format (e.g., if previous months used "email" and "article", try "blog post", "review", or "notice").`;
}

export function buildReadingComprehensionPrompt(ctx: BlockPromptContext): string {
  const source = SOURCE_LANGUAGES.find((l) => l.code === ctx.profile.native_language);
  const target = TARGET_LANGUAGES.find((l) => l.code === ctx.profile.target_language);

  return `Generate a **Reading Comprehension** exercise block for a ${ctx.profile.level.toUpperCase()} ${target?.name} learner whose native language is ${source?.name}.

## Theme
Workbook theme: "${ctx.workbookTheme}"
Sub-theme for this block: "${ctx.subTheme}"

## Block Position
This is block ${ctx.blockOrder} of ${ctx.totalBlocks}.
${buildPersonalizationClause(ctx.profile, ctx.blockOrder, ctx.isPreview)}

## Requirements

1. Write a **100-200 word passage** in ${target?.name} related to "${ctx.subTheme}".
2. Choose a realistic \`textType\`: "email", "article", "blog post", "menu", "sign", "text message", "letter", "review", or "notice".
3. The passage difficulty must match ${ctx.profile.level.toUpperCase()}.
4. Generate **5 comprehension questions** with a MIX of types:
   - At least 1 \`open\` question (free-text answer)
   - At least 1 \`multiple_choice\` question (3 options, exactly 1 correct)
   - At least 1 \`true_false\` question
5. Each question has:
   - \`question\` in ${target?.name}
   - \`questionNative\` translation in ${source?.name}
   - \`answer\` — the correct answer (full sentence for open, option text for MC, "Verdadero"/"Falso" for T/F)
   - \`options\` array ONLY for multiple_choice (3 items, one must match \`answer\`)
6. Questions should test comprehension, not just word-finding. Include inference questions.

## Output Format

Return a single JSON object matching this TypeScript interface:

\`\`\`typescript
interface ReadingComprehensionBlock {
  id: string;
  type: "reading_comprehension";
  order: ${ctx.blockOrder};
  title: string;               // In ${target?.name}
  titleNative: string;         // In ${source?.name}
  theme: "${ctx.subTheme}";
  estimatedPages: 2;
  text: string;                // The passage in ${target?.name}
  textType: string;            // e.g., "email", "article"
  questions: Array<{
    question: string;          // In ${target?.name}
    questionNative: string;    // In ${source?.name}
    answer: string;
    type: "open" | "true_false" | "multiple_choice";
    options?: string[];        // Only for multiple_choice, exactly 3 items
  }>;
}
\`\`\`

Respond with ONLY the JSON object.${buildProgressionClause(ctx)}${buildExampleClause(ctx, 'reading_comprehension')}${buildCrossMonthClause(ctx)}${buildPreviousReadingThemesClause(ctx)}`;
}
